import { Component } from "@theme/component";
import { debounce, onDocumentLoaded } from "@theme/utilities";
import { MegaMenuHoverEvent } from "@theme/events";

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 * @property {HTMLElement} scrollContainer - The scrollable container.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ["overflowMenu", "scrollContainer"];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener("pointerleave", () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(() => {
      this.#preloadImages();
      this.#initializeScrollButtons();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(":hover") ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute("data-animating");

    if (!(event.target instanceof Element)) return;

    // Don't activate if clicking on "All" menu item
    const allMenuItem = event.target.closest("[data-categories-trigger-header]");
    if (allMenuItem) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === "";

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = "false";
    }

    this.#state.activeItem = item;
    this.ariaExpanded = "true";
    item.ariaExpanded = "true";

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty("--submenu-height", `${submenuHeight}px`);
    this.style.setProperty("--submenu-opacity", "1");

    // Calculate and set position for mega-menu dropdowns (left or right aligned)
    if (submenu && submenu.querySelector(".mega-menu--dropdown")) {
      const listItem = item?.closest(".menu-list__list-item");

      if (listItem && item) {
        const listItemRect = listItem.getBoundingClientRect();
        const linkRect = item.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const linkCenterX = linkRect.left + linkRect.width / 2;

        // Estimate dropdown width (use max-width as estimate)
        const estimatedDropdownWidth = 850; // max-width from CSS
        const spaceOnRight = viewportWidth - linkRect.left;
        const spaceOnLeft = linkRect.right;

        // Determine alignment: if item is on right side of viewport, align from right
        const isRightSide = linkCenterX > viewportWidth / 2;
        // Also check if there's more space on the left when on right side
        const shouldAlignRight = isRightSide || (spaceOnRight < estimatedDropdownWidth && spaceOnLeft >= estimatedDropdownWidth);

        if (shouldAlignRight) {
          // Align from right edge of menu item
          const rightOffset = listItemRect.right - linkRect.right;
          submenu.style.setProperty("--mega-menu-right-offset", `${rightOffset}px`);
          submenu.style.setProperty("--mega-menu-left-offset", "auto");
          submenu.setAttribute("data-align-right", "true");
        } else {
          // Align from left edge of menu item
          const leftOffset = linkRect.left - listItemRect.left;
          submenu.style.setProperty("--mega-menu-left-offset", `${leftOffset}px`);
          submenu.style.setProperty("--mega-menu-right-offset", "auto");
          submenu.removeAttribute("data-align-right");
        }
      }
    }
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty("--submenu-height", "0px");
    this.style.setProperty("--submenu-opacity", "0");
    this.dataset.overflowExpanded = "false";

    this.#state.activeItem = null;
    this.ariaExpanded = "false";
    item.ariaExpanded = "false";
    item.setAttribute("data-animating", "");

    setTimeout(() => {
      item.removeAttribute("data-animating");
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]:not(.menu-list__icon)');
    images?.forEach((image) => image.removeAttribute("loading"));
  };

  /**
   * Initialize scroll buttons functionality
   */
  #initializeScrollButtons = () => {
    const scrollContainer = this.refs.scrollContainer;
    if (!scrollContainer) return;

    const leftButton = this.querySelector(".menu-list__scroll-button--left");
    const rightButton = this.querySelector(".menu-list__scroll-button--right");

    if (!leftButton || !rightButton) return;

    // Get the overflow-list element
    const overflowList = scrollContainer.querySelector("overflow-list");

    // Get the actual scrollable element (inside shadow DOM)
    const getScrollableElement = () => {
      if (overflowList?.shadowRoot) {
        const list = overflowList.shadowRoot.querySelector('[part="list"]');
        if (list instanceof HTMLElement) {
          // Make the list scrollable and prevent wrapping
          list.style.overflowX = "auto";
          list.style.overflowY = "hidden";
          list.style.scrollBehavior = "smooth";
          list.style.scrollbarWidth = "none";
          // @ts-ignore - IE/Edge specific property
          list.style.msOverflowStyle = "none";
          list.style.flexWrap = "nowrap";
          list.style.whiteSpace = "nowrap";
          list.style.height = "auto";
          list.style.minHeight = "auto";
          // @ts-ignore - Webkit specific property
          if (list.style.webkitOverflowScrolling !== undefined) {
            // @ts-ignore - Webkit specific property
            list.style.webkitOverflowScrolling = "touch";
          }
          return list;
        }
      }
      return scrollContainer instanceof HTMLElement ? scrollContainer : null;
    };

    // Wait for overflow-list to initialize, then setup scrolling
    const setupScrolling = () => {
      const scrollElement = getScrollableElement();
      if (!scrollElement) {
        // Retry if not ready yet
        setTimeout(setupScrolling, 50);
        return;
      }

      // Disable overflow-list's reflow to keep all items visible
      if (overflowList) {
        overflowList.setAttribute("disabled", "true");

        // Move any items from overflow slot back to default
        const overflowItems = overflowList.querySelectorAll('[slot="overflow"]');
        overflowItems.forEach((item) => {
          item.removeAttribute("slot");
        });
      }

      setupScrollButtons(scrollElement);
    };

    /**
     * Setup scroll buttons for the scrollable element
     * @param {HTMLElement} scrollElement
     */
    const setupScrollButtons = (scrollElement) => {
      if (!scrollElement) return;
      const updateButtonVisibility = () => {
        const scrollLeft = scrollElement.scrollLeft;
        const scrollWidth = scrollElement.scrollWidth;
        const clientWidth = scrollElement.clientWidth;
        const maxScroll = scrollWidth - clientWidth;

        // Show/hide left button
        if (scrollLeft > 0) {
          leftButton.classList.add("menu-list__scroll-button--visible");
        } else {
          leftButton.classList.remove("menu-list__scroll-button--visible");
        }

        // Show/hide right button
        if (scrollLeft < maxScroll - 1) {
          // -1 for rounding errors
          rightButton.classList.add("menu-list__scroll-button--visible");
        } else {
          rightButton.classList.remove("menu-list__scroll-button--visible");
        }
      };

      // Check if chevron scroll behavior should be enabled (set via Liquid template)
      // Works on index and cohort-pages pages
      // The data attribute is set on the nav element with header-menu attribute
      // Check this element first, then look for nav element inside or closest
      let dataAttr = this.getAttribute("data-is-index-page");
      if (!dataAttr) {
        // If not found on this, look for nav element with header-menu attribute
        const navElement = this.querySelector("nav[header-menu]") || this.closest("nav[header-menu]");
        if (navElement) {
          dataAttr = navElement.getAttribute("data-is-index-page");
        }
      }
      const isIndexPage = dataAttr === "true";

      // Function to update chevron visibility based on vertical page scroll (only on index page)
      const updateChevronVisibility = () => {
        if (!isIndexPage) return;
        const scrollY = window.scrollY || window.pageYOffset;
        const chevrons = this.querySelectorAll(".menu-list__chevron");
        chevrons.forEach((chevron) => {
          if (scrollY > 0) {
            chevron.classList.add("menu-list__chevron--visible");
          } else {
            chevron.classList.remove("menu-list__chevron--visible");
          }
        });
      };

      // Scroll left
      leftButton.addEventListener(
        "click",
        () => {
          const scrollAmount = scrollElement.clientWidth * 0.8;
          scrollElement.scrollBy({
            left: -scrollAmount,
            behavior: "smooth",
          });
        },
        { signal: this.#abortController.signal }
      );

      // Scroll right
      rightButton.addEventListener(
        "click",
        () => {
          const scrollAmount = scrollElement.clientWidth * 0.8;
          scrollElement.scrollBy({
            left: scrollAmount,
            behavior: "smooth",
          });
        },
        { signal: this.#abortController.signal }
      );

      // Update button visibility on scroll
      scrollElement.addEventListener("scroll", updateButtonVisibility, {
        signal: this.#abortController.signal,
      });

      // Update chevron visibility on window scroll (vertical) - only on index page
      if (isIndexPage) {
        window.addEventListener("scroll", updateChevronVisibility, {
          signal: this.#abortController.signal,
        });
      }

      // Update on resize
      const resizeObserver = new ResizeObserver(() => {
        updateButtonVisibility();
      });
      resizeObserver.observe(scrollElement);

      // Initial check
      setTimeout(updateButtonVisibility, 100);
      if (isIndexPage) {
        setTimeout(updateChevronVisibility, 100);
      }
    };

    // Start setup
    setupScrolling();
  };
}

if (!customElements.get("header-menu")) {
  customElements.define("header-menu", HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
